import React, { useState } from 'react';
import { supabase } from './supabase';
import { useTheme } from './ThemeContext';

function Login({ onAuthSuccess }) {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const INVITE_CODE = process.env.REACT_APP_INVITE_CODE;

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Validate invite code
        if (!INVITE_CODE) {
          throw new Error('Invite code system is not configured');
        }
        if (inviteCode.trim().toLowerCase() !== INVITE_CODE.toLowerCase()) {
          throw new Error('Invalid invite code');
        }

        // Validate password match
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Validate password strength
        if (password.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: displayName,
            }
          }
        });
        if (error) throw error;
        alert('Check your email for confirmation link!');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      <div style={styles.authBox}>
        <div style={styles.logo}>S</div>
        <h1 style={styles.title}>Syntari</h1>
        <p style={styles.subtitle}>
          {isSignUp ? 'Create your account' : 'Your AI-powered voice companion'}
        </p>

        {isSignUp && (
          <div style={styles.inviteHint}>
            You need an invite code to sign up
          </div>
        )}

        <form onSubmit={handleAuth} style={styles.form}>
          {isSignUp && (
            <input
              type="text"
              placeholder="Invite Code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              style={styles.input}
              required
              disabled={loading}
              autoComplete="off"
            />
          )}
          {isSignUp && (
            <input
              type="text"
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={styles.input}
              required
              disabled={loading}
              autoComplete="name"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
            disabled={loading}
            minLength={isSignUp ? 8 : undefined}
          />
          {isSignUp && (
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
              disabled={loading}
              minLength={8}
            />
          )}
          {error && <p style={styles.error}>{error}</p>}
          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine}></div>
        </div>

        <div style={styles.googleDisabledContainer}>
          <button
            style={styles.googleButtonDisabled}
            disabled={true}
          >
            <svg style={styles.googleIconSvg} viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <p style={styles.googleDisabledText}>
            Google sign-in is disabled during testing
          </p>
        </div>

        <p style={styles.toggle}>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <span
            onClick={() => !loading && setIsSignUp(!isSignUp)}
            style={{
              ...styles.link,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </span>
        </p>
      </div>
    </div>
  );
}

const getStyles = (theme) => ({
  container: {
    minHeight: '100vh',
    backgroundColor: theme.background,
    color: theme.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  authBox: {
    backgroundColor: theme.surface,
    padding: '36px 28px',
    borderRadius: '20px',
    maxWidth: '420px',
    width: '100%',
    border: `1px solid ${theme.border}`,
    textAlign: 'center',
    boxShadow: theme.background === '#FFFFFF' ? '0 8px 24px rgba(0, 0, 0, 0.06)' : '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  logo: {
    width: '72px',
    height: '72px',
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    borderRadius: '20px',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '34px',
    fontWeight: '800',
    color: '#FFFFFF',
    boxShadow: `0 8px 24px rgba(0, 212, 106, 0.35)`,
  },
  title: {
    fontSize: '26px',
    fontWeight: '700',
    marginBottom: '8px',
    color: theme.text,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    color: theme.textMuted,
    fontSize: '14px',
    marginBottom: '28px',
    lineHeight: '1.4',
  },
  inviteHint: {
    backgroundColor: 'rgba(0, 212, 106, 0.1)',
    color: theme.primary,
    fontSize: '13px',
    padding: '10px 14px',
    borderRadius: '10px',
    marginBottom: '16px',
    border: `1px solid ${theme.primary}`,
    fontWeight: '500',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  input: {
    padding: '15px 18px',
    fontSize: '16px',
    borderRadius: '12px',
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    outline: 'none',
    transition: 'border-color 0.2s',
    WebkitAppearance: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  button: {
    padding: '15px 18px',
    fontSize: '16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: theme.primary,
    color: '#FFFFFF',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: `0 4px 16px rgba(0, 212, 106, 0.35)`,
    width: '100%',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    gap: '12px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.border,
  },
  dividerText: {
    color: theme.textMuted,
    fontSize: '13px',
    fontWeight: '500',
  },
  googleDisabledContainer: {
    width: '100%',
  },
  googleButtonDisabled: {
    width: '100%',
    padding: '15px 18px',
    fontSize: '15px',
    borderRadius: '12px',
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.textMuted,
    cursor: 'not-allowed',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    boxSizing: 'border-box',
    opacity: 0.5,
  },
  googleDisabledText: {
    fontSize: '12px',
    color: theme.textMuted,
    textAlign: 'center',
    marginTop: '8px',
    marginBottom: 0,
  },
  googleButton: {
    width: '100%',
    padding: '15px 18px',
    fontSize: '15px',
    borderRadius: '12px',
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.surface,
    color: theme.text,
    cursor: 'pointer',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s, border-color 0.2s',
    boxSizing: 'border-box',
  },
  googleIconSvg: {
    flexShrink: 0,
  },
  toggle: {
    marginTop: '24px',
    fontSize: '14px',
    color: theme.textTertiary,
    lineHeight: '1.5',
  },
  link: {
    color: theme.primary,
    cursor: 'pointer',
    fontWeight: '600',
  },
  error: {
    color: theme.error,
    fontSize: '14px',
    textAlign: 'center',
    padding: '12px',
    backgroundColor: 'rgba(255, 68, 68, 0.1)',
    borderRadius: '10px',
    marginBottom: '4px',
  },
});

export default Login;
